// The "All classes" screen of the Tokenization overview (C-7): league tables
// across every class board and stock venue (networks, issuers, platforms,
// asset classes), each product counted once, and the products most recently
// added to tracking. Stablecoins are left out unless asked for: at $300B+
// they would swamp every other row. The math lives in league.ts.

import { useEffect, useMemo, useState } from "react";

import type { VenueLoad } from "../all-venues";
import { API_BASE } from "../api";
import { ASSET_CLASSES, fetchClassBoard, fetchClassIndex, isBoardClass, representedLine, type BoardClassId, type ClassBoard, type ClassIndex } from "../classes";
import { RepresentedTile } from "./RepresentedTile";
import { csvFileName, downloadCsv, toCsv, type CsvColumn } from "../csv";
import { changeChip } from "../class-history";
import { flowFor, flowText, flowTone } from "../flows";
import { compactUsd, count, signedPercent } from "../format";
import { league, LEAGUE_TABS, recentlyAdded, type AddedItem, type LeagueRow, type LeagueTab, type RecentAdditions } from "../league";
import { fetchStablecoins, type Stablecoins } from "../stablecoins";
import styles from "./AllVenues.module.css";
import own from "./CrossClass.module.css";
import { HistoryPanel } from "./HistoryPanel";

const ROWS_SHOWN = 25;
export const REGISTRY_CLASSES = ASSET_CLASSES.filter((c) => isBoardClass(c.id)).map((c) => c.apiId as BoardClassId);

const pct = (value: number) => `${(value * 100).toFixed(value >= 0.1 ? 1 : 2)}%`;

/** /v1/classes, every registry board and (on request) the stablecoin board, loaded once. */
export function useLeagueData(includeStablecoins: boolean) {
  const [index, setIndex] = useState<ClassIndex | null>(null);
  const [boards, setBoards] = useState<ClassBoard[]>([]);
  const [stablecoins, setStablecoins] = useState<Stablecoins | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchClassIndex(controller.signal)
      .then(setIndex)
      .catch((e: unknown) => controller.signal.aborted || setError(e instanceof Error ? e.message : String(e)));
    for (const id of REGISTRY_CLASSES) {
      fetchClassBoard(id, controller.signal)
        .then((board) => setBoards((current) => [...current.filter((b) => b.asset_class !== board.asset_class), board]))
        .catch(() => undefined); // A missing board is left out; the table says how many classes it covers.
    }
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!includeStablecoins || stablecoins) return;
    const controller = new AbortController();
    fetchStablecoins(controller.signal)
      .then(setStablecoins)
      .catch(() => undefined);
    return () => controller.abort();
  }, [includeStablecoins, stablecoins]);

  return { index, boards, stablecoins, error };
}

function useRecentAdditions() {
  const [recent, setRecent] = useState<RecentAdditions | null>(null);
  const [recentFailed, setRecentFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/v1/classes/recent-additions?limit=50`, { signal: controller.signal })
      .then((r) => (r.ok ? (r.json() as Promise<RecentAdditions>) : Promise.reject(new Error(`The API answered ${r.status}`))))
      .then((data) => (data?.schema === "panther.recent-additions.v1" && Array.isArray(data.items) ? setRecent(data) : setRecentFailed(true)))
      .catch(() => controller.signal.aborted || setRecentFailed(true));
    return () => controller.abort();
  }, []);
  return { recent, recentFailed };
}

export function LeagueTable({ rows, showChange, showFlow }: { rows: LeagueRow[]; showChange: boolean; showFlow: boolean }) {
  let rank = 0;
  return (
    <div aria-label="League table" className={styles.scroll} role="region" tabIndex={0}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Name</th>
            <th className={own.num} scope="col">Products</th>
            <th className={own.num} scope="col">Value</th>
            <th scope="col">Share</th>
            {showChange ? (
              <th className={own.num} scope="col">
                7d
              </th>
            ) : null}
            {showFlow ? (
              <th className={own.num} scope="col">
                Net flow · 7d
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={styles.rank}>{row.unranked ? "" : ++rank}</td>
              <td>
                {row.href ? (
                  <a className={own.name} href={row.href}>
                    {row.name}
                  </a>
                ) : (
                  <span className={own.nameText}>{row.name}</span>
                )}
                {row.detail ? <span className={styles.sub}>{row.detail}</span> : null}
              </td>
              <td className={own.num}>{row.products === null ? <span className={styles.absent}>—</span> : count(row.products)}</td>
              <td className={own.num}>{compactUsd(row.valueUsd)}</td>
              <td>
                {row.share === null ? (
                  <span className={styles.absent}>—</span>
                ) : (
                  <span className={styles.share}>
                    <span aria-hidden className={styles.shareTrack}>
                      <span className={styles.shareFill} style={{ width: `${Math.min(100, row.share * 100)}%` }} />
                    </span>
                    {pct(row.share)}
                  </span>
                )}
              </td>
              {showChange ? (
                <td className={own.num}>
                  {row.change7d === null ? (
                    <span className={styles.absent} title="Needs 7 days of daily snapshots">
                      no history yet
                    </span>
                  ) : (
                    <span className={row.change7d > 0 ? "up" : row.change7d < 0 ? "down" : undefined}>{signedPercent(row.change7d)}</span>
                  )}
                </td>
              ) : null}
              {showFlow ? (
                <td className={own.num}>
                  {row.flow7d == null ? <span className={styles.absent}>not measured</span> : <span className={flowTone(row.flow7d) ?? undefined}>{flowText(row.flow7d)}</span>}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LEAGUE_CSV = (showChange: boolean, showFlow: boolean): Array<CsvColumn<LeagueRow & { rank: number | null }>> => [
  { header: "Rank", value: (r) => r.rank },
  { header: "Name", value: (r) => r.name },
  { header: "Detail", value: (r) => r.detail },
  { header: "Products", value: (r) => r.products },
  { header: "Value (USD)", value: (r) => r.valueUsd },
  { header: "Share of headline (fraction)", value: (r) => r.share },
  ...(showChange ? [{ header: "7d change (fraction)", value: (r: LeagueRow) => r.change7d }] : []),
  ...(showFlow ? [{ header: "Net flow 7d (USD)", value: (r: LeagueRow) => r.flow7d ?? null }] : []),
];

const FEED_SHOWN = 20;

function RecentlyAdded({ items, loading, failed }: { items: AddedItem[]; loading: boolean; failed: boolean }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, FEED_SHOWN);
  return (
    <section aria-label="Recently added" className={`panel ${styles.panel}`}>
      <header className="panel-head">
        <h2>Recently added</h2>
        <span>products newly tracked, newest first · starting sets left out</span>
      </header>
      {items.length === 0 ? (
        <p className="panel-empty">{failed ? "Recent additions are unavailable right now." : loading ? "Reading recent additions…" : "Nothing added to tracking yet."}</p>
      ) : (
        <>
        <div aria-label="Recently added products" className={styles.scroll} role="region" tabIndex={0}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Added</th>
                <th scope="col">Product</th>
                <th scope="col">Class</th>
                <th className={own.num} scope="col">
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((item) => (
                <tr data-group={item.group || undefined} key={item.key}>
                  <td className={own.date}>{item.date}</td>
                  <td>
                    {item.href ? (
                      <a className={own.name} href={item.href} title={`Open ${item.name}`}>
                        {item.name}
                      </a>
                    ) : (
                      <span className={own.nameText}>{item.name}</span>
                    )}
                    {item.detail ? <span className={styles.sub}>{item.detail}</span> : null}
                  </td>
                  <td className={styles.muted}>{item.classLabel}</td>
                  <td className={own.num}>{item.valueUsd === null ? <span className={styles.absent}>{item.group ? "batch" : "not valued"}</span> : compactUsd(item.valueUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length > FEED_SHOWN ? (
          <button className={styles.more} onClick={() => setAll(!all)} type="button">
            {all ? `Show the newest ${FEED_SHOWN}` : `Show all ${count(items.length)}`}
          </button>
        ) : null}
        </>
      )}
    </section>
  );
}

export function CrossClass({ venues }: { venues: readonly VenueLoad[] }) {
  const [tab, setTab] = useState<LeagueTab>("networks");
  const [includeStablecoins, setIncludeStablecoins] = useState(false);
  const [all, setAll] = useState(false);
  const { index, boards, stablecoins, error } = useLeagueData(includeStablecoins);
  const { recent, recentFailed } = useRecentAdditions();

  const input = index ? { index, boards, venues, stablecoins: includeStablecoins ? stablecoins : null } : null;
  const table = useMemo(() => (input ? league(tab, input) : null), [tab, input?.index, input?.boards, input?.venues, input?.stablecoins]);
  const added = useMemo(() => (recent ? recentlyAdded(recent) : []), [recent]);
  const waitingStablecoins = includeStablecoins && !stablecoins;
  const showChange = Boolean(table?.rows.some((r) => r.change7d !== null));
  const showFlow = Boolean(table?.rows.some((r) => r.flow7d != null));
  const headlineFlow7d = index ? flowFor(index, "7d") : null;
  const shown = table ? (all ? table.rows : table.rows.slice(0, ROWS_SHOWN)) : [];
  const venuesReady = venues.filter((v) => v.board).length;

  function exportCsv(): void {
    if (!table || !index) return;
    let rank = 0;
    // Every row of the table: the top-25 cut is paging, not a filter.
    const rows = table.rows.map((row) => ({ ...row, rank: row.unranked ? null : ++rank }));
    const name = `league-${tab}${includeStablecoins ? "-with-stablecoins" : ""}`;
    downloadCsv(csvFileName(name, index.as_of), toCsv(rows, LEAGUE_CSV(showChange, showFlow)));
  }

  return (
    <>
      {index ? (
        <ul className="tiles">
          {index.total_value_usd != null ? (
            <RepresentedTile
              complete
              label="All classes"
              line={representedLine(index)}
              note={
                <>
                  each product counted once
                  {changeChip(index.change_7d) ? (
                    <span className={changeChip(index.change_7d)!.tone ?? undefined}> · {changeChip(index.change_7d)!.text} 7d</span>
                  ) : null}
                </>
              }
              value={compactUsd(index.total_value_usd)}
            />
          ) : null}
          {headlineFlow7d !== null ? (
          <li className="tile">
            <span className="tile-label">Net flows · 7d</span>
            <span className={`tile-value${flowTone(headlineFlow7d) ? ` ${flowTone(headlineFlow7d)}` : ""}`}>{flowText(headlineFlow7d)}</span>
            <span className="tile-note">every class, units issued minus redeemed</span>
          </li>
          ) : null}
        </ul>
      ) : null}
      <HistoryPanel chartTitle="All classes, each product counted once, USD" subject={{ kind: "headline" }} title="All classes over time" />
      <section aria-label="League tables" className={`panel ${styles.panel}`}>
        <header className="panel-head">
          <h2>League tables · every class</h2>
          <span>
            {index ? `${count(boards.length + 1)} of ${count(REGISTRY_CLASSES.length + 1)} classes · ${count(venuesReady)} stock venues` : "loading"}
            {includeStablecoins ? " · with stablecoins" : ""}
          </span>
        </header>
        <div className={own.toolbar}>
          <span aria-label="League" className={own.tabs} role="group">
            {LEAGUE_TABS.map(([id, label]) => (
              <button aria-pressed={tab === id} className={own.tab} data-active={tab === id} key={id} onClick={() => setTab(id)} type="button">
                {label}
              </button>
            ))}
          </span>
          <label className={own.toggle}>
            <input checked={includeStablecoins} onChange={(e) => setIncludeStablecoins(e.target.checked)} type="checkbox" />
            Include stablecoins
          </label>
          <button className={own.csv} disabled={!table?.rows.length} onClick={exportCsv} type="button">
            Download CSV
          </button>
        </div>
        {error ? (
          <p className="panel-empty">
            <span className="error">Data unavailable right now: {error}</span>
          </p>
        ) : !table || waitingStablecoins ? (
          <p className="panel-empty">{waitingStablecoins ? "Reading the stablecoin board…" : "Reading every class board…"}</p>
        ) : table.rows.length === 0 ? (
          <p className="panel-empty">Not enough data yet.</p>
        ) : (
          <>
            <LeagueTable rows={shown} showChange={showChange} showFlow={showFlow} />
            {table.rows.length > ROWS_SHOWN ? (
              <button className={styles.more} onClick={() => setAll(!all)} type="button">
                {all ? `Show the top ${ROWS_SHOWN}` : `Show all ${count(table.rows.length)}`}
              </button>
            ) : null}
          </>
        )}
        {table ? (
          <p className={styles.footnote}>
            Share is of {includeStablecoins ? "the all-class headline" : "every class but stablecoins"}
            {table.headline !== null ? ` (${compactUsd(table.headline)})` : ""}, each product counted once.
            {table.notes.length ? ` ${table.notes.join(" ")}` : ""}
          </p>
        ) : null}
      </section>
      <RecentlyAdded failed={recentFailed} items={added} loading={recent === null} />
    </>
  );
}
